import api from 'configs/api'
import type { Dispatch, SetStateAction } from 'react'
import {
  formatCnpj,
  formatDocument,
  getClosingDate,
  getInvoiceDeadline,
  getPaymentMonth,
  getSpecialtyLabel,
  isMonthClosed,
  onlyAlphanumeric,
  onlyDigits,
} from './utils'
import {
  ConsultationRowStatus,
  IBankAccountData,
  IFinancialConsultationsStats,
  IFinancialInvoiceRow,
  IFinancialMonthAppointments,
  IFinancialMonthDetail,
  IFinancialMonthlyProduction,
  IFinancialSummary,
  IInfoContent,
  IMonthlyConsultationRow,
  InvoiceStatus,
  MonthlyProductionStatus,
} from './types'

interface ISettlementAmount {
  consultation: number
  cancel_lt_30: number
  no_show: number
}

interface ISettlement {
  id: string
  period_month: string
  total_amount: string
  amount: ISettlementAmount
  amount_60: ISettlementAmount
}

interface IHistoryMeta {
  total: number
  page: number
  limit: number
  total_page: number
}

interface IPaymentHistoryApiResponse {
  settlements: ISettlement[]
  meta: IHistoryMeta
}

interface IByMonthApiResponse {
  data: {
    month: string
    total_amount: string
    amount: ISettlementAmount
    amount_60: ISettlementAmount
    pending: number
  }
}

interface IMonthlyProductionApiItem {
  period_month: string
  total_amount: number
}

interface IAppointmentApiItem {
  id: string
  status: string
  from_date_time: string
  to_date_time: string
  user_name: string
  cancel_description: string
  cancel_at: string
  cancel_platform: string
  specialty_name: string
  specialty_slug: string
  amount: number
  time: number
  quantity: number
  event_type: string
}

interface IAppointmentsByMonthMeta {
  total: number
  page: number
  limit: number
  totalPages: number
  nextPage: boolean
}

interface IAppointmentsByMonthApiResponse {
  data: {
    month: string
    total_amount: string
    amount_consultation: string
    amount_no_show: string
    amount_cancel_lt_30: string
    amount_hours: string | null
    amount: ISettlementAmount
    amount_60: ISettlementAmount
    appointments: IAppointmentApiItem[]
  }
  meta: IAppointmentsByMonthMeta
}

interface IDoctorUserApiResponse {
  pool_category_id: string | null
}

interface IInfoBlockApiResponse {
  title: string
  description: string
  list_order: number
}

interface IInfoApiResponse {
  key: string
  title: string
  blocks: IInfoBlockApiResponse[]
}

interface IPoolCategoryApiResponse {
  id: string
  name: string
}

interface IBankAccountApiResponse {
  id: string
  user_id: string
  holder_name: string
  document: string
  razao_social: string | null
  national_registration: string | null
  bank_code: string
  bank_name: string
  account_type: string
  agency: string
  account: string
  pix_key: string | null
  pix_key_type: string | null
  created_at: string
  updated_at: string
}

interface IBankAccountUpdatePayload {
  holder_name: string
  document: string
  razao_social?: string
  national_registration?: string
  bank_code: string
  bank_name: string
  account_type: string
  agency: string
  account: string
  pix_key?: string
  pix_key_type?: string
}

const getDoctorId = (): string => {
  const raw = window.localStorage.getItem('@StarBem:user')
  return JSON.parse(raw!).id
}

const emptySummary: IFinancialSummary = {
  totalValue: 0,
  openConsultations: 0,
  closingDate: null,
  invoiceDeadline: null,
  paymentMonth: null,
  stats: {
    finished30: 0,
    finished60: 0,
    noShow30: 0,
    noShow60: 0,
    canceledUnder30_30: 0,
    canceledUnder30_60: 0,
  },
}

const toSafeNumber = (value: unknown): number => {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

const mapSettlementStats = (
  amount?: Partial<ISettlementAmount> | null,
  amount_60?: Partial<ISettlementAmount> | null
): IFinancialConsultationsStats => ({
  finished30: toSafeNumber(amount?.consultation),
  finished60: toSafeNumber(amount_60?.consultation),
  noShow30: toSafeNumber(amount?.no_show),
  noShow60: toSafeNumber(amount_60?.no_show),
  canceledUnder30_30: toSafeNumber(amount?.cancel_lt_30),
  canceledUnder30_60: toSafeNumber(amount_60?.cancel_lt_30),
})

const mapSettlement = (s: ISettlement): IFinancialMonthDetail => ({
  periodMonth: s.period_month,
  totalValue: parseFloat(s.total_amount) || 0,
  closingDate: getClosingDate(s.period_month),
  invoiceDeadline: getInvoiceDeadline(s.period_month),
  paymentMonth: getPaymentMonth(s.period_month),
  isClosed: isMonthClosed(s.period_month),
  stats: mapSettlementStats(s.amount, s.amount_60),
})

export const findMonthlySummary = async (
  setSummary: Dispatch<SetStateAction<IFinancialSummary>>,
  month: string,
  setLoading?: Dispatch<SetStateAction<boolean>>
): Promise<void> => {
  try {
    setLoading?.(true)
    const id = getDoctorId()
    const { data: res } = await api.get<IByMonthApiResponse>(
      `/doctors/ms/v1/${id}/payment/report/by-month?month=${month}`
    )
    setSummary({
      totalValue: parseFloat(res.data.total_amount) || 0,
      openConsultations: toSafeNumber(res.data.pending),
      closingDate: getClosingDate(month),
      invoiceDeadline: getInvoiceDeadline(month),
      paymentMonth: getPaymentMonth(month),
      stats: mapSettlementStats(res.data.amount, res.data.amount_60),
    })
  } catch (err) {
    console.error(err)
    setSummary({ ...emptySummary })
  } finally {
    setLoading?.(false)
  }
}

export const findFinancialMonthHistory = async (
  limit: number,
  setHistory: Dispatch<SetStateAction<IFinancialMonthDetail[]>>,
  setLoading?: Dispatch<SetStateAction<boolean>>
): Promise<void> => {
  try {
    setLoading?.(true)
    const id = getDoctorId()
    const { data } = await api.get<IPaymentHistoryApiResponse>(
      `/doctors/ms/v1/${id}/payment/history?page=1&limit=${limit}`
    )
    setHistory(data.settlements.map(mapSettlement))
  } catch (err) {
    console.error(err)
    setHistory([])
  } finally {
    setLoading?.(false)
  }
}

const EVENT_TYPE_STATUS: Record<string, ConsultationRowStatus> = {
  consultation: 'attended',
  no_show: 'noShow',
  cancel_lt_30: 'canceled',
}

const mapAppointmentRow = (
  item: IAppointmentApiItem
): IMonthlyConsultationRow => ({
  id: item.id,
  datetime: item.from_date_time.replace(' ', 'T'),
  patientName: item.user_name,
  specialty: getSpecialtyLabel(item.specialty_name, item.specialty_slug),
  durationMinutes: item.time,
  value: item.amount,
  status: EVENT_TYPE_STATUS[item.event_type] ?? 'attended',
})

const emptyAppointments = (
  periodMonth: string,
  limit: number
): IFinancialMonthAppointments => ({
  periodMonth,
  totalValue: 0,
  stats: {
    finished30: 0,
    finished60: 0,
    noShow30: 0,
    noShow60: 0,
    canceledUnder30_30: 0,
    canceledUnder30_60: 0,
  },
  rates: {
    consultation30: 0,
    consultation60: 0,
    noShow30: 0,
    noShow60: 0,
    canceled30: 0,
    canceled60: 0,
  },
  rows: [],
  meta: { total: 0, page: 1, limit, totalPages: 1, nextPage: false },
})

export const fetchMonthlyAppointmentsForExport = async (
  month: string,
  limit: number
): Promise<IMonthlyConsultationRow[]> => {
  const id = getDoctorId()
  const { data: res } = await api.get<IAppointmentsByMonthApiResponse>(
    `/doctors/ms/v1/${id}/payment/appointments/by-month?month=${month}&page=1&limit=${limit}`
  )

  return (res.data.appointments ?? []).map(mapAppointmentRow)
}

export const findMonthlyAppointments = async (
  month: string,
  page: number,
  limit: number,
  setResult: Dispatch<SetStateAction<IFinancialMonthAppointments>>,
  setLoading?: Dispatch<SetStateAction<boolean>>
): Promise<void> => {
  try {
    setLoading?.(true)
    const id = getDoctorId()
    const { data: res } = await api.get<IAppointmentsByMonthApiResponse>(
      `/doctors/ms/v1/${id}/payment/appointments/by-month?month=${month}&page=${page}&limit=${limit}`
    )

    const consultation30 = toSafeNumber(
      parseFloat(res.data.amount_consultation)
    )
    const noShow30 = toSafeNumber(parseFloat(res.data.amount_no_show))
    const canceled30 = toSafeNumber(parseFloat(res.data.amount_cancel_lt_30))
    const consultation60 = res.data.amount_hours
      ? toSafeNumber(parseFloat(res.data.amount_hours))
      : consultation30 * 2

    setResult({
      periodMonth: res.data.month,
      totalValue: toSafeNumber(parseFloat(res.data.total_amount)),
      stats: mapSettlementStats(res.data.amount, res.data.amount_60),
      rates: {
        consultation30,
        consultation60,
        noShow30,
        noShow60: noShow30 * 2,
        canceled30,
        canceled60: canceled30 * 2,
      },
      rows: (res.data.appointments ?? []).map(mapAppointmentRow),
      meta: res.meta,
    })
  } catch (err) {
    console.error(err)
    setResult(emptyAppointments(month, limit))
  } finally {
    setLoading?.(false)
  }
}

const getMonthlyProductionStatus = (
  periodMonth: string,
  currentMonth: string
): MonthlyProductionStatus => {
  if (periodMonth === currentMonth) return 'current'
  if (periodMonth > currentMonth) return 'future'
  return new Date(`${getPaymentMonth(periodMonth)}-01`) <= new Date()
    ? 'paid'
    : 'closed'
}

export const findMonthlyProduction = async (
  visibleMonths: string[],
  setProduction: Dispatch<SetStateAction<IFinancialMonthlyProduction[]>>,
  setLoading?: Dispatch<SetStateAction<boolean>>
): Promise<void> => {
  try {
    setLoading?.(true)
    const id = getDoctorId()
    const { data } = await api.get<IMonthlyProductionApiItem[]>(
      `/doctors/ms/v1/${id}/payment/report/lasts-month`
    )
    const totalByMonth = new Map(
      data.map((item) => [item.period_month, item.total_amount])
    )
    const now = new Date()
    const currentMonth = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, '0')}`

    setProduction(
      visibleMonths.map((periodMonth) => ({
        periodMonth,
        totalValue: totalByMonth.get(periodMonth) ?? 0,
        status: getMonthlyProductionStatus(periodMonth, currentMonth),
        paymentMonth: getPaymentMonth(periodMonth),
      }))
    )
  } catch (err) {
    console.error(err)
    setProduction([])
  } finally {
    setLoading?.(false)
  }
}

export const findInfoByKey = async (
  key: string,
  setInfo: Dispatch<SetStateAction<IInfoContent | null>>,
  setLoading?: Dispatch<SetStateAction<boolean>>
): Promise<void> => {
  try {
    setLoading?.(true)
    const { data } = await api.get<IInfoApiResponse>(
      `/doctors/ms/v1/info/${key}`
    )
    setInfo({
      key: data.key,
      title: data.title,
      blocks: data.blocks
        .map((block) => ({
          title: block.title,
          description: block.description,
          order: block.list_order,
        }))
        .sort((a, b) => a.order - b.order),
    })
  } catch (err) {
    console.error(err)
    setInfo(null)
  } finally {
    setLoading?.(false)
  }
}

const mapBankAccountResponse = (
  data: IBankAccountApiResponse
): IBankAccountData => ({
  holderName: data.holder_name,
  cpf: formatDocument(data.document),
  companyName: data.razao_social ?? '',
  cnpj: data.national_registration
    ? formatCnpj(data.national_registration)
    : '',
  bankCode: data.bank_code,
  bankName: data.bank_name,
  accountType: data.account_type,
  agency: data.agency,
  accountNumber: data.account,
  pixKeyType: data.pix_key_type ?? '',
  pixKey: data.pix_key ?? '',
})

const mapBankAccountToPayload = (
  form: IBankAccountData
): IBankAccountUpdatePayload => {
  const pixKey = form.pixKey.trim()

  return {
    holder_name: form.holderName,
    document: onlyDigits(form.cpf),
    ...(form.companyName ? { razao_social: form.companyName } : {}),
    ...(form.cnpj
      ? { national_registration: onlyAlphanumeric(form.cnpj) }
      : {}),
    bank_code: form.bankCode,
    bank_name: form.bankName,
    account_type: form.accountType,
    agency: form.agency,
    account: form.accountNumber,
    ...(pixKey ? { pix_key: pixKey, pix_key_type: form.pixKeyType } : {}),
  }
}

export const findBankAccountData = async (
  setAccount: Dispatch<SetStateAction<IBankAccountData | null>>,
  setLoading?: Dispatch<SetStateAction<boolean>>,
  setLoadError?: Dispatch<SetStateAction<boolean>>
): Promise<void> => {
  try {
    setLoading?.(true)
    setLoadError?.(false)
    const { data } = await api.get<IBankAccountApiResponse | null>(
      '/doctors/ms/v1/me/bank-account'
    )
    setAccount(data ? mapBankAccountResponse(data) : null)
  } catch (err) {
    console.error(err)
    setAccount(null)
    setLoadError?.(true)
  } finally {
    setLoading?.(false)
  }
}

export const updateBankAccountData = async (
  form: IBankAccountData
): Promise<IBankAccountData> => {
  const payload = mapBankAccountToPayload(form)
  const { data } = await api.put<IBankAccountApiResponse>(
    '/doctors/ms/v1/me/bank-account',
    payload
  )

  return mapBankAccountResponse(data)
}

export const findDoctorPlan = async (
  setPlan: Dispatch<SetStateAction<string | null>>,
  setLoading?: Dispatch<SetStateAction<boolean>>
): Promise<void> => {
  try {
    setLoading?.(true)
    const [{ data: user }, { data: categories }] = await Promise.all([
      api.get<IDoctorUserApiResponse>('/doctors/ms/v1/me'),
      api.get<IPoolCategoryApiResponse[]>(
        '/doctors/ms/v1/pool/categories?type=all'
      ),
    ])
    const plan = categories.find(
      (category) => category.id === user.pool_category_id
    )
    setPlan(plan?.name ?? null)
  } catch (err) {
    console.error(err)
    setPlan(null)
  } finally {
    setLoading?.(false)
  }
}

interface ISettlementInvoiceDetailApiResponse {
  id: string
  file_name: string
  file_url: string
  status: string
  sent_at: string
  drive_file_id: string
}

interface ISettlementInvoiceApiItem {
  settlement_id: string
  period_month: string
  total_amount: string
  status: string
  created_at: string
  invoice: ISettlementInvoiceDetailApiResponse | null
}

interface ISettlementInvoiceApiMeta {
  total: number
  page: number
  limit: number
  totalPages: number
}

interface ISettlementInvoiceApiResponse {
  settlements: ISettlementInvoiceApiItem[]
  meta: ISettlementInvoiceApiMeta
}

const getInvoiceRowStatus = (
  item: ISettlementInvoiceApiItem
): InvoiceStatus => {
  if (item.status !== 'closed') return 'pending'
  return item.invoice ? 'sent' : 'available'
}

const mapSettlementInvoiceRow = (
  item: ISettlementInvoiceApiItem
): IFinancialInvoiceRow => ({
  settlementId: item.settlement_id,
  periodMonth: item.period_month,
  totalValue: parseFloat(item.total_amount) || 0,
  status: getInvoiceRowStatus(item),
  invoiceFileName: item.invoice?.file_name ?? null,
  invoiceFileUrl: item.invoice?.file_url ?? null,
})

export const findInvoices = async (
  page: number,
  limit: number,
  setInvoices: Dispatch<SetStateAction<IFinancialInvoiceRow[]>>,
  setLoading?: Dispatch<SetStateAction<boolean>>
): Promise<void> => {
  try {
    setLoading?.(true)
    const id = getDoctorId()
    const { data } = await api.get<ISettlementInvoiceApiResponse>(
      `/doctors/ms/v1/${id}/settlements/invoice?page=${page}&limit=${limit}`
    )
    setInvoices(data.settlements.map(mapSettlementInvoiceRow))
  } catch (err) {
    console.error(err)
    setInvoices([])
  } finally {
    setLoading?.(false)
  }
}

export const uploadSettlementInvoice = async (
  settlementId: string,
  file: File
): Promise<void> => {
  const formData = new FormData()
  formData.append('file', file)
  await api.post(`/doctors/ms/v1/settlements/${settlementId}/invoice`, formData)
}

export const replaceSettlementInvoice = async (
  settlementId: string,
  file: File
): Promise<void> => {
  const formData = new FormData()
  formData.append('file', file)
  await api.put(`/doctors/ms/v1/settlements/${settlementId}/invoice`, formData)
}
